import { Button } from "primereact/button";
import { Message } from "primereact/message";

import {
  ICareSettingNotCovered,
  INetworkStatusOverride,
  IOutOfNetworkException,
  buildCareSettingNotCoveredMessage,
  buildNetworkStatusMessage,
  buildOutOfNetworkExceptionMessage,
  canApproveOutOfNetworkException,
} from "../../../eligibilityMessages";
import { requestOutOfNetworkException } from "../../../outOfNetworkExceptionEvent";

/**
 * CLE-2683 / CLE-2882 / CLE-2883 — the network-status notice under the benefit fields, rendered in both the
 * primary and secondary panels from the stored eligibility response, so a reopened order shows the same
 * thing as a fresh check.
 *
 *   · out of network, figures shown      → warning, no button
 *   · out of network, nothing to show    → warning + "Exception is approved" where the API allows it
 *   · exception approved                 → info message, no warning, no button
 *
 * The button re-runs eligibility for this side with `outOfNetworkExceptionApproved: true`
 * (`EligibilityButton` listens for the request). Undo is the normal Run Eligibility button.
 *
 * CLE-2922 — also the "care setting not covered" warning: the payer reports the account's own care setting not
 * covered, so the engine left those fields blank. Same style, no button — it warns and blocks nothing.
 */
export function NetworkStatusBanner({
  networkStatusOverride,
  outOfNetworkException,
  careSettingNotCovered,
  isSecondaryInsurance = false,
}: {
  networkStatusOverride?: INetworkStatusOverride | null;
  outOfNetworkException?: IOutOfNetworkException | null;
  careSettingNotCovered?: ICareSettingNotCovered | null;
  isSecondaryInsurance?: boolean;
}) {
  const exceptionMessage = buildOutOfNetworkExceptionMessage(
    outOfNetworkException
  );
  const notCoveredMessage = buildCareSettingNotCoveredMessage(
    careSettingNotCovered
  );
  const networkMessage = buildNetworkStatusMessage(networkStatusOverride);
  const canApprove = canApproveOutOfNetworkException(networkStatusOverride);

  return (
    <>
      {exceptionMessage && (
        <Message severity="info" className="mt-2" text={exceptionMessage} />
      )}
      {networkMessage && (
        <div className="mt-2 flex flex-column align-items-start gap-2">
          <Message severity="warn" text={networkMessage} />
          {canApprove && (
            <Button
              type="button"
              label="Exception is approved"
              icon="pi pi-check"
              className="p-button-sm p-button-outlined"
              onClick={() => requestOutOfNetworkException(isSecondaryInsurance)}
            />
          )}
        </div>
      )}
      {notCoveredMessage && (
        <Message severity="warn" className="mt-2" text={notCoveredMessage} />
      )}
    </>
  );
}
